import { useEffect, useRef, useState } from 'react';
import { Box, useTokens } from '@gemini/core';

import { loadScript } from '../../helpers/loadScript';
import thor from '../../src/app/services/thor';

export type TViewMode = 'normal' | 'compact';

interface IProfiCardMFE extends HTMLElement {
    numberOfAgents: number;
    excludedAgents: string[];
    zipCodes: string[];
    proximity: boolean;
    viewMode: TViewMode;
    userResearchCoordinates: {
        latitude: number;
        longitude: number;
    };
    trackingPosition: string;
    trackingType: string;
    hasConsent: boolean;
}

export interface IProfiCardProps {
    zipCode: string;
    coordinates: {
        latitude: number;
        longitude: number;
    };
}

const ProfiCard = ({ zipCode, coordinates }: IProfiCardProps) => {
    const profiCardRef = useRef<IProfiCardMFE | null>(null);
    const [isEmptyProfiCard, setIsEmptyProfiCard] = useState(false);
    const tokens = useTokens();

    useEffect(() => {
        let timer: NodeJS.Timeout;
        if (process.env['NX_PROFI_CARD_URL']) {
            loadScript(
                process.env['NX_PROFI_CARD_URL'],
                () => {
                    timer = setTimeout(() => {
                        const profiCardElement = profiCardRef.current;
                        const profiCardContent =
                            profiCardElement?.shadowRoot?.querySelector('#app > div');

                        if (!profiCardContent?.innerHTML.trim()) {
                            setIsEmptyProfiCard(true);
                        }
                    }, 5000);
                },
                (error) => {
                    setIsEmptyProfiCard(true);
                    // implement here if we need to display script loading error
                    console.warn(error);
                },
            );
        }
        return () => {
            clearTimeout(timer);
        };
    }, []);

    const setProfiCardProps = () => {
        if (profiCardRef.current) {
            profiCardRef.current.numberOfAgents = 3;
            profiCardRef.current.excludedAgents = [''];
            profiCardRef.current.zipCodes = [zipCode];
            profiCardRef.current.proximity = true;
            profiCardRef.current.viewMode = 'compact';
            profiCardRef.current.trackingPosition = 'estimate_confirmation'; // targeted page
            profiCardRef.current.trackingType = 'thor'; // track data via gtm
            profiCardRef.current.hasConsent = thor.hasConsent; // Didomi consent or other user consent
            profiCardRef.current.userResearchCoordinates = coordinates;
        }
    };

    useEffect(() => {
        // profi-card event specifications are available here:
        // https://avivgroup.atlassian.net/wiki/spaces/ProDev/pages/223581972/AVO+Profi+Card+Web+component#AVOProfiCardWebcomponent-Events
        if (profiCardRef.current) {
            profiCardRef.current.addEventListener('on-mounted', setProfiCardProps);
        }
        return () => {
            if (profiCardRef.current) {
                profiCardRef.current.removeEventListener('on-mounted', setProfiCardProps);
            }
        };
    }, [profiCardRef]);

    if (isEmptyProfiCard) {
        return null;
    }
    return (
        <Box
            width="100%"
            unsafe_className="card-container"
            borderRadius="radius.8"
            borderWidth="borderWidth.1"
            borderStyle="solid"
            borderColor="color.border.light.default"
            minWidth={{
                'breakpoint.base': 0,
                'breakpoint.md': `calc((780px - ${tokens['spacing.32']} - ${tokens['spacing.32']} - ${tokens['spacing.24']}) / 2)`,
            }}
        >
            <profi-card ref={profiCardRef} />
        </Box>
    );
};

export default ProfiCard;
